import { getApiClient, getRequestHeaders } from './zodios-client';
import type { ApiKey } from '@/types';

const STORAGE_KEY = 'lazily_api_key';
const COOKIE_NAME = 'lazily_api_key';
const COOKIE_MAX_DAYS = 30;

export const authService = {
  // Store API key in localStorage
  storeApiKey(apiKey: string): void {
    localStorage.setItem(STORAGE_KEY, apiKey);
    // Also store in cookie for persistence across refreshes
    try {
      const expires = new Date(Date.now() + COOKIE_MAX_DAYS * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(apiKey)}; expires=${expires}; path=/; SameSite=Lax`;
    } catch {}
  },

  // Get API key from localStorage
  getStoredApiKey(): string | null {
    const ls = localStorage.getItem(STORAGE_KEY);
    if (ls) return ls;
    // Fallback to cookie
    try {
      const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  },

  // Remove API key from localStorage
  clearStoredApiKey(): void {
    localStorage.removeItem(STORAGE_KEY);
    try {
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    } catch {}
  },

  // Validate API key format (basic validation)
  validateApiKeyFormat(apiKey: string): boolean {
    // Basic validation - API keys should be non-empty strings
    return typeof apiKey === 'string' && apiKey.trim().length > 0;
  },

  // Test API key by making a simple request (admin-only dashboard)
  async testApiKey(apiKey: string): Promise<{ isValid: boolean; isAdmin: boolean; error?: string }> {
    try {
      const client = getApiClient();
      const headers = getRequestHeaders();
      
      // Debug: Log the current headers
      console.log('Current API client headers:', headers);
      
      // Test admin access by listing tenants
      await client.listTenants({ headers });
      console.log('Admin access confirmed');
      return { isValid: true, isAdmin: true };
    } catch (error: any) {
      console.error('API key test failed:', error);
      return { 
        isValid: false, 
        isAdmin: false, 
        error: error.message || 'Invalid API key' 
      };
    }
  },

  // Note: Admin status is determined in testApiKey method
  // No separate getCurrentUserInfo needed
};
